import React from "react";
import { Link } from "react-router-dom";
import BrandLockup from "@/components/BrandLockup";
import SubscribeForm from "@/components/SubscribeForm";
import { DAN_SOCIAL } from "@/lib/links";

const SOCIALS = [
  { key: "spotify", label: "Spotify", icon: "fa-brands fa-spotify", href: DAN_SOCIAL.spotify },
  { key: "youtube", label: "YouTube", icon: "fa-brands fa-youtube", href: DAN_SOCIAL.youtube },
  { key: "instagram", label: "Instagram", icon: "fa-brands fa-instagram", href: DAN_SOCIAL.instagram },
  { key: "apple-music", label: "Apple Music", icon: "fa-brands fa-apple", href: DAN_SOCIAL.appleMusic },
];

const LEGAL = [
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/patrons", label: "Support the Music" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms & Conditions" },
];

/**
 * One footer for the whole site: lockup, subscribe block, loud social row,
 * then the small print.
 */
export default function SiteFooter() {
  return (
    <footer className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-10 pb-14 mt-10" data-testid="site-footer">
      <div
        className="h-px w-full"
        style={{ background: "linear-gradient(90deg, transparent, #F2D589, transparent)", opacity: 0.45 }}
      />

      <div className="mt-12 grid gap-12 md:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] items-center">
        <div className="flex md:justify-start">
          <div className="flex flex-col items-center gap-6">
            <BrandLockup variant="stacked" markSize={68} to={null} testId="footer-lockup" />
            <div className="flex items-center gap-4" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.key}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  data-testid={`footer-${s.key}`}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full text-base transition-colors hover:bg-[#F2D589] hover:text-[#080419]"
                  style={{ border: "1px solid rgba(242,213,137,0.45)", color: "#F2D589" }}
                >
                  <i className={s.icon} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div data-testid="footer-subscribe-block">
          <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
            Behind the scenes
          </p>
          <h3 className="text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Go behind the scenes with Dan.
          </h3>
          <p className="mt-3 text-sm" style={{ color: "rgba(245,239,227,0.7)", lineHeight: 1.7 }}>
            New recordings, upcoming shows, soundbath dates, and the occasional
            story from the road.
          </p>
          <div className="mt-6 max-w-[30rem]">
            <SubscribeForm source="footer" buttonLabel="Send it" testIdPrefix="footer-subscribe" />
          </div>
        </div>
      </div>

      {/* Small print */}
      <div
        className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs"
        style={{ color: "rgba(245,239,227,0.5)" }}
      >
        <span>&copy; {new Date().getFullYear()} Dan Berry.</span>
        {LEGAL.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="transition-colors hover:text-[#F2D589]"
            style={{ color: "inherit" }}
            data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {l.label}
          </Link>
        ))}
        <a
          href="mailto:hello@danberrymusic.com"
          className="transition-colors hover:text-[#F2D589]"
          style={{ color: "inherit" }}
          data-testid="footer-email"
        >
          hello@danberrymusic.com
        </a>
      </div>
    </footer>
  );
}
